import { useTranslation } from "react-i18next";

import type { ExamSectionBreakdown } from "@/lib/exam-api";

/**
 * apps/web :: components/exam :: ExamSectionBreakdownChart
 *
 * ROADS-430 — accessible horizontal bar chart that shows per-category
 * accuracy for a single exam attempt.
 *
 * A11y choices (WCAG 2.2 AA):
 *   - The whole chart is a single `role="list"` with each bar a
 *     `role="listitem"`. Screen readers walk it deterministically and
 *     announce one row per category.
 *   - Each bar carries an `aria-label` that fully describes it:
 *     "Right-of-way: 50% (2 of 4)." — i.e. percent AND raw counts AND
 *     a "weak" / "ok" status. Color is decorative, never the primary
 *     status channel (Hard Rule #7).
 *   - Numeric value is also rendered as visible text on the row, so
 *     sighted low-vision users don't have to compare bar lengths.
 *   - Bars meet 4.5:1 contrast against the surface. The "weak" bars
 *     use brand-danger; "ok" bars use brand-primary.
 *   - Container uses CSS logical properties (no left/right) so the
 *     chart mirrors cleanly under `<html dir="rtl">`.
 *   - We render unanswered rows with `aria-hidden="false"` and an
 *     explicit "0 of N" label so the visual "empty bar" is announced.
 *
 * Sort order: defers to whatever the server returns. The backend sorts
 * alphabetical by category for stable presentation.
 */
export interface ExamSectionBreakdownChartProps {
  breakdown: ExamSectionBreakdown[];
  /** Strict accuracy threshold (0..1). Anything strictly less is rendered "weak". */
  weakThreshold?: number;
}

const DEFAULT_WEAK_THRESHOLD = 0.7;

export default function ExamSectionBreakdownChart({
  breakdown,
  weakThreshold = DEFAULT_WEAK_THRESHOLD,
}: ExamSectionBreakdownChartProps) {
  const { t } = useTranslation();

  if (breakdown.length === 0) {
    return (
      <p
        data-testid="exam-breakdown-empty"
        className="text-sm text-neutral-600"
      >
        {t("exam.results.breakdownEmpty")}
      </p>
    );
  }

  return (
    <ul
      role="list"
      data-testid="exam-breakdown-chart"
      aria-labelledby="exam-breakdown-heading"
      className="flex flex-col gap-sm"
    >
      {breakdown.map((row) => {
        const percent = Math.round(row.accuracy * 100);
        const categoryLabel =
          t(`exam.results.sectionLabels.${row.category}`, {
            defaultValue: row.category,
          }) ?? row.category;
        const isWeak =
          row.answered >= 3 && row.accuracy < weakThreshold;

        const barClass = isWeak
          ? "bg-danger"
          : "bg-brand-primary";

        const labelText = t("exam.results.breakdownBarLabel", {
          category: categoryLabel,
          percent,
          correct: row.correct,
          answered: row.answered,
        });

        return (
          <li
            key={row.category}
            role="listitem"
            data-testid={`exam-breakdown-row-${row.category}`}
            data-status={isWeak ? "weak" : "ok"}
            aria-label={labelText}
            className="flex flex-col gap-xs"
          >
            <div className="flex items-baseline justify-between gap-sm text-sm">
              <span className="font-semibold text-brand-primary">
                {categoryLabel}
              </span>
              <span
                data-testid={`exam-breakdown-row-${row.category}-value`}
                className="text-xs text-neutral-700"
              >
                {percent}% &middot; {row.correct}/{row.answered}
              </span>
            </div>
            <div
              role="presentation"
              aria-hidden="true"
              className="h-3 w-full rounded-full bg-brand-surface/60"
            >
              <div
                data-testid={`exam-breakdown-row-${row.category}-bar`}
                className={`h-3 rounded-full transition-[width] duration-500 ${barClass}`}
                style={{ width: `${percent}%`, minWidth: percent > 0 ? "0.5rem" : 0 }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
